import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment/moment";
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as images from "../../assets/image";
import { deleteUser, getUsers, archiveUser, sendEmailBlast } from "../../services/services";
import { constant } from "../../utils/constants";
import Pagination from "../../utils/Pagination";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { FaFolder, FaEnvelope } from "react-icons/fa";
import { CiFilter } from "react-icons/ci";
import FilterModal from "../../common/FilterModal";

const Client = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(constant.PAGE_NO_ONE);
  const [filterShow, setFilterShow] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [isToggled, setIsToggled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tableContainerRef = useRef(null);

  const { data, refetch } = useQuery({
    queryKey: ["client-list", page, filterData, isToggled],
    queryFn: async () => {
      let params = {
        page: page,
        limit: constant.PAGE_LIMIT,
        role: constant.ROLE.CLIENT,
        sortedBy: constant.SORTED_BY.FIRST_NAME,
        sortBy: constant.SORT_BY.ASC,
      };
      if (isToggled) {
        params.archivedUsers = true;
      }
      if (
        Object.keys(filterData).length &&
        Object.keys(filterData).filter((item) => filterData[item]).length
      ) {
        params.range = `${filterData.start}TO${filterData.end}`;
      }
      if (search) params.search = search;
      let resp = await getUsers(params);
      return resp?.data?.data ?? {};
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => deleteUser(id),
    onSuccess: () => {
      Swal.fire({
        title: "Deleted!",
        text: "User has been deleted.",
        icon: "success",
        confirmButtonColor: "#0d1227",
      });
      refetch();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id) => {
      const user = data?.users?.find((user) => user._id === id);
      const isCurrentlyArchived = user?.isArchived || false;
      return await archiveUser(id, { archive: !isCurrentlyArchived });
    },
    onSuccess: () => {
      Swal.fire({
        title: "Success!",
        text: "User archive status updated.",
        icon: "success",
        confirmButtonColor: "#0d1227",
      });
      refetch();
    },
    onError: (error) => {
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to update archive status",
        icon: "error",
        confirmButtonColor: "#0d1227",
      });
    },
  });

  const emailBlastMutation = useMutation({
    mutationFn: async ({ selected, message }) => {
      return await sendEmailBlast({ selected, message });
    },
    onSuccess: () => {
      Swal.fire({
        title: "Success!",
        text: "Email blast sent successfully.",
        icon: "success",
        confirmButtonColor: "#0d1227",
      });
    },
    onError: (error) => {
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to send email blast",
        icon: "error",
        confirmButtonColor: "#0d1227",
      });
    },
  });

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) deleteMutation.mutate(id);
    });
  };

  const handleArchive = (id) => {
    const user = data?.users?.find((user) => user._id === id);
    const isCurrentlyArchived = user?.isArchived || false;

    Swal.fire({
      title: "Are you sure?",
      text: `This client will be ${isCurrentlyArchived ? "unarchived" : "archived"}!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0d1227",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${isCurrentlyArchived ? "unarchive" : "archive"} it!`,
    }).then((result) => {
      if (result.isConfirmed) {
        archiveMutation.mutate(id);
      }
    });
  };

 const handleEmailBlast = () => {
  Swal.fire({
    title: "Send Email Blast",
    html: `
      <div style="text-align: left; max-height: 450px;">
        <label style="font-weight: bold; margin-bottom: 10px; display: block;">Select Clients:</label>
        <div style="margin-bottom: 10px;">
          <button id="select-all" style="padding: 5px 10px; margin-right: 10px; background-color: #0d1227; color: white; border: none; border-radius: 4px; cursor: pointer;">Select All</button>
          <button id="deselect-all" style="padding: 5px 10px; background-color: #d33; color: white; border: none; border-radius: 4px; cursor: not-allowed; opacity: 0.5;" disabled>Deselect All</button>
        </div>
        <div id="client-checkboxes" style="margin-bottom: 15px; max-height: 200px; overflow-y: auto;">
          ${data?.users?.length > 0
            ? data.users
                .map(
                  (user) => `
                    <div style="margin: 5px 0;">
                      <input type="checkbox" id="client-${user._id}" value="${user.email}" style="margin-right: 10px;" class="client-checkbox">
                      <label for="client-${user._id}">${user?.firstName} ${user?.lastName || ''} (${user?.email})</label>
                    </div>
                  `
                )
                .join("")
            : "<p>No clients available</p>"
          }
        </div>
        <label style="font-weight: bold; margin-bottom: 10px; display: block;">Message:</label>
        <textarea id="email-message" style="width: 100%; height: 100px; resize: none; padding: 10px;" placeholder="Type your message here..."></textarea>
      </div>
    `,
    width: 600,
    showCancelButton: true,
    confirmButtonColor: "#0d1227",
    cancelButtonColor: "#d33",
    confirmButtonText: "Send",
    cancelButtonText: "Cancel",
    didOpen: () => {
      const deselectAllButton = document.getElementById("deselect-all");
      const checkboxes = document.querySelectorAll("#client-checkboxes input.client-checkbox");

      // Function to update the Deselect All button state
      const updateDeselectButtonState = () => {
        const anyChecked = Array.from(checkboxes).some((checkbox) => checkbox.checked);
        deselectAllButton.disabled = !anyChecked;
        deselectAllButton.style.opacity = anyChecked ? "1" : "0.5";
        deselectAllButton.style.cursor = anyChecked ? "pointer" : "not-allowed";
      };

      // Initial state check
      updateDeselectButtonState();

      // Add event listeners to checkboxes to update button state
      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", updateDeselectButtonState);
      });

      // Select All button functionality
      document.getElementById("select-all").addEventListener("click", () => {
        checkboxes.forEach((checkbox) => {
          checkbox.checked = true;
        });
        updateDeselectButtonState();
      });

      // Deselect All button functionality
      document.getElementById("deselect-all").addEventListener("click", () => {
        checkboxes.forEach((checkbox) => {
          checkbox.checked = false;
        });
        updateDeselectButtonState();
      });
    },
    preConfirm: () => {
      const selectedClients = Array.from(
        document.querySelectorAll("#client-checkboxes input.client-checkbox:checked")
      )
        .map((checkbox) => checkbox.value)
        .filter((value) => value !== "on" && value);

      const message = document.getElementById("email-message").value;

      if (!selectedClients.length) {
        Swal.showValidationMessage("Please select at least one client.");
        return false;
      }
      if (!message.trim()) {
        Swal.showValidationMessage("Please enter a message.");
        return false;
      }

      return { selectedClients, message };
    },
  }).then((result) => {
    if (result.isConfirmed) {
      emailBlastMutation.mutate({
        selected: result.value.selectedClients,
        message: result.value.message,
      });
    }
  });
};

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      console.log("isMobile:", window.innerWidth < 768, "Window Width:", window.innerWidth);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <section className="main-content schedules_bar">
        <div className="commonSearchBar mt-3">
          <h4 className="heading20">Client Management</h4>
          <div className="endContent clientManegement">
            <div className={`toggle-container ${isMobile ? "mb-3" : ""}`}>
              <div
                className={`toggle-switch ${isToggled ? "toggled" : ""}`}
                onClick={() => setIsToggled(!isToggled)}
              >
                <div className={`slider ${isToggled ? "toggled" : ""}`}></div>
              </div>
              <label className="label-text">Show Archived</label>
            </div>
            <div className="clientSearch">
              <img
                src={images.Search}
                alt="searchImg"
                className="clientSearchIcon"
              />
              <input
                type="text"
                placeholder="Type and press enter to search."
                className="clientSearchInput"
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onKeyDown={(e) =>
                  e.key == "Enter" && search.trim() !== "" && refetch()
                }
                onKeyUp={(e) =>
                  e.target.value == "" && e.key == "Backspace" && refetch()
                }
              />
            </div>
            <div className="position-relative">
              {Object.keys(filterData).length &&
              Object.keys(filterData).filter((item) => filterData[item]).length ? (
                <div className="redDot" />
              ) : (
                ""
              )}
              <button
                className="greenBtn calendar-btn"
                type="button"
                title="Filter By"
                onClick={() => setFilterShow(true)}
              >
                <CiFilter size={35} />
              </button>
            </div>
            <button
              className="greenBtn calendar-btn"
              type="button"
              title="Send Email Blast"
              onClick={handleEmailBlast}
            >
              <FaEnvelope size={25} />
            </button>
            <Link className="greenBtn" to="../client/add">
              Add Client
            </Link>
          </div>
        </div>

        <div className="container-fluid p-0">
          <div className="row mt-4">
            <div className="col-md-12">
              <div
                ref={tableContainerRef}
                style={{
                  overflowX: isMobile ? "auto" : "visible",
                  WebkitOverflowScrolling: "touch",
                  width: "100%",
                  position: "relative",
                  paddingBottom: isMobile ? "20px" : "0",
                }}
              >
                {isMobile && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "0",
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "#666",
                      fontSize: "12px",
                    }}
                  >
                    ← Scroll →
                  </div>
                )}
                <table
                  style={{
                    width: "random",
                    minWidth: isMobile ? "800px" : "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                  }}
                >
                  <thead style={{ backgroundColor: "#f8f9fa" }}>
                    <tr>
                      <th
                        className="tableHeading"
                        style={{
                          position: isMobile ? "sticky" : "static",
                          left: isMobile ? "0" : "auto",
                          zIndex: isMobile ? "4" : "auto",
                          boxShadow: isMobile
                            ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                            : "none",
                          minWidth: isMobile ? "100px" : "120px",
                          borderRadius: "10px 0px 0px",
                        }}
                      >
                        {isMobile ? (
                          <>
                            <div>Client</div>
                            <div>Name</div>
                          </>
                        ) : (
                          "Client Name"
                        )}
                      </th>
                      <th className="tableHeading">DOB</th>
                      <th className="tableHeading">Email</th>
                      <th
                        className="tableHeading"
                        style={{ minWidth: isMobile ? "100px" : "120px" }}
                      >
                        Phone Number
                      </th>
                      <th
                        className="tableHeading"
                        style={{ minWidth: isMobile ? "150px" : "200px" }}
                      >
                        Address
                      </th>
                      <th className="tableHeading">Authorized Employees</th>
                      <th className="tableHeading">Created At</th>
                      <th
                        className="tableHeading"
                        style={{
                          minWidth: "120px",
                          borderRadius: "0px 10px 0px 0px",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.users && data?.users?.length > 0 ? (
                      data?.users?.map((item, index) => {
                        const isArchived = item?.isArchived || false;
                        return (
                          <tr key={`user_${index}`}>
                            <td
                              style={{
                                position: isMobile ? "sticky" : "static",
                                left: isMobile ? "0" : "auto",
                                zIndex: isMobile ? "4" : "auto",
                                backgroundColor: isMobile
                                  ? "white"
                                  : "transparent",
                                boxShadow: isMobile
                                  ? "2px 0 5px -2px rgba(0,0,0,0.1)"
                                  : "none",
                                minWidth: isMobile ? "100px" : "120px",
                                textOverflow: isMobile ? "initial" : "ellipsis",
                                whiteSpace: isMobile ? "normal" : "nowrap",
                                overflow: isMobile ? "visible" : "hidden",
                                wordBreak: isMobile ? "break-word" : "normal",
                              }}
                              className="text-capitalize"
                              title={
                                isMobile
                                  ? ""
                                  : `${item?.firstName} ${item?.lastName}`
                              }
                            >
                              <Link
                                to={`../client/${item?._id}`}
                                style={{ textDecoration: "none", color: "inherit" }}
                                title={`View ${item?.firstName} ${item?.lastName}'s details`}
                                aria-label={`View ${item?.firstName} ${item?.lastName}'s details`}
                              >
                                {isMobile ? (
                                  <>
                                    <div>{item?.firstName}</div>
                                    <div>{item?.lastName}</div>
                                  </>
                                ) : (
                                  `${item?.firstName} ${item?.lastName}`
                                )}
                              </Link>
                            </td>
                            <td className="text-capitalize">
                              {item?.dob
                                ? `${moment(item?.dob).format("ll")}`
                                : "N/A"}
                            </td>
                            <td>{item?.email}</td>
                            <td
                              style={{ minWidth: isMobile ? "100px" : "120px" }}
                              className="text-nowrap"
                            >
                              {formatPhoneNumberIntl(item?.phoneNumber)}
                            </td>
                            <td style={{ minWidth: isMobile ? "150px" : "200px" }}>
                              {`${item?.userAddress?.address}, ${item?.userAddress?.city}, ${item?.userAddress?.state}, ${item?.userAddress?.zipCode}`}
                            </td>
                            <td>
                              {item?.authorizedDetails?.length
                                ? item?.authorizedDetails
                                    ?.map((i) => `${i.firstName} ${i.lastName}`)
                                    ?.join(", ")
                                : "N/A"}
                            </td>
                            <td>
                              {moment(item?.createdAt).format(
                                "ddd, MMM DD, YYYY - hh:mm A"
                              )}
                            </td>
                            <td style={{ minWidth: "120px" }}>
                              <div className="action-buttons">
                                <Link to={`../client/add?id=${item?._id}`}>
                                  <img
                                    src={images.edit}
                                    className="ms-2 editImg"
                                    alt="Edit"
                                    role="button"
                                  />
                                </Link>
                                <FaFolder
                                  className="ms-2 editImg archive-icon"
                                  onClick={() => handleArchive(item?._id)}
                                  style={{
                                    cursor: "pointer",
                                    width: "20px",
                                    height: "20px",
                                    color: isArchived ? "grey" : "black",
                                  }}
                                  title={isArchived ? "Unarchive" : "Archive"}
                                />
                                <img
                                  src={images.deleteImg}
                                  className="ms-2 editImg"
                                  alt="Delete"
                                  role="button"
                                  onClick={() => handleDelete(item?._id)}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td style={{ textAlign: "center" }} colSpan={8}>
                          No Data Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={page}
                totalPages={data?.total_pages}
                setPage={setPage}
              />
            </div>
          </div>
        </div>
        {filterShow && (
          <FilterModal
            show={filterShow}
            setShow={setFilterShow}
            refetch={refetch}
            data={filterData}
            setData={setFilterData}
          />
        )}
      </section>
    </>
  );
};

export default Client;